import { tutorialStage } from "@frontier/content/tutorial";
import type { TutorialState } from "@frontier/content/tutorial";
import type { ModuleId, ShipSnapshot } from "@frontier/protocol/contracts";
import { moduleLabels } from "./ui-labels";

export interface TutorialGuidance {
  message: string;
  alert: boolean;
}

export function hasTutorialGuidance(
  tutorial: TutorialState | null,
): tutorial is TutorialState {
  return tutorial !== null && !tutorial.docked;
}

export function tutorialThreat(
  attacker: Pick<ShipSnapshot, "targetId" | "telegraph" | "dead">,
  playerId: string,
): boolean {
  return (
    !attacker.dead && attacker.telegraph > 0 && attacker.targetId === playerId
  );
}

export function tutorialGuidance(
  tutorial: TutorialState | null,
  warning: boolean,
  primary: ModuleId,
  secondary: ModuleId,
  currentRegionId?: string,
): TutorialGuidance | null {
  if (!hasTutorialGuidance(tutorial)) return null;
  const trainingMessages = {
    moved: "Долети до маяка: нажми на него мышью или направь джойстик.",
    stationVisited: "Подлети к учебному доку и нажми «СТЫКОВКА».",
    contractAccepted: "В доке выбери и прими учебный контракт.",
    targeted: "Нажми на отмеченного налётчика, чтобы выбрать цель.",
    fired: "Подлети ближе к цели — корабль начнёт стрелять сам.",
    defended: `Дождись красного предупреждения и нажми «${moduleLabels.barrier}».`,
    helped: "Теперь уничтожь налётчика и помоги транспорту.",
    cargoCollected: "Подлети к контейнеру и нажми «ЗАБРАТЬ ГРУЗ».",
    contractDelivered: "Вернись в учебный док и доставь груз.",
    moduleEquipped: `Установи полученный «${moduleLabels.volley}» в ячейку корабля.`,
    moduleUsed: `Орудие стреляет само. Для усиленного выстрела выбери мишень, подлети и нажми «${moduleLabels.volley}» внизу экрана.`,
  };
  if (tutorial.version === 2) {
    const stage = tutorialStage(tutorial);
    if (stage === "complete") return null;
    if (warning && stage === "defended") return { message: `Красная атака! Нажми подсвеченный «${moduleLabels.barrier}».`, alert: true };
    if (stage === "return") return { message: "Обучение завершено. Под станцией — три телепорта на базы фракций, они мигают на карте сектора. Подлети к выбранному и перейди. Выбор фракции постоянный.", alert: false };
    return { message: trainingMessages[stage], alert: false };
  }
  const barrierKey =
    primary === "barrier" ? "Q" : secondary === "barrier" ? "2" : "";
  if (warning && !tutorial.milestones.includes("defended"))
    return {
      message: `Красная атака! Включи «${moduleLabels.barrier}»${barrierKey ? ` (${barrierKey})` : ""}. Прими удар, пока барьер активен.`,
      alert: true,
    };
  const stage = tutorialStage(tutorial);
  if (stage === "complete") return null;
  if (currentRegionId === "patrol" && stage !== "return")
    return {
      message: "Здесь безопасно: можно добывать астероиды. Для обучения пройди через врата в дальнем углу в Поле обломков.",
      alert: false,
    };
  if (stage === "return")
    return {
      message: tutorial.rewardGranted
        ? tutorial.route === "training" ? `${moduleLabels.volley} получен. Выбери фракцию порталом.` : `${moduleLabels.volley} получен. Вернись на станцию.`
        : "Помощь подтверждается. Дождись награды.",
      alert: false,
    };
  const messages = {
    ...trainingMessages,
    moved: "Нажми на пустое место — корабль долетит до точки. Удерживай ЛКМ — веди курсором. Джойстик / WASD — тоже можно.",
    targeted: "Лети к маркеру «Налётчик». Увидишь нападающего — нажми на него или его имя справа.",
    fired: "Держи противника перед носом: пушка стреляет сама.",
    defended: `Перед красной атакой включи «${moduleLabels.barrier}»${barrierKey ? ` (${barrierKey})` : ""}. Прими удар, пока барьер активен.`,
    helped: "Добей нападающего и помоги транспорту.",
  };
  return { message: messages[stage], alert: false };
}
